import React, { useEffect, useState } from 'react';
import { Table, Switch, Input, Button, message } from 'antd';
import axios from 'axios';

const recipientsToString = (recipients) => (Array.isArray(recipients) ? recipients.join(', ') : '');

const recipientsToArray = (value) =>
    (value || '')
        .split(',')
        .map((email) => email.trim())
        .filter(Boolean);

const QUIET = { suppressErrorFlash: true };

const firstRecipientsError = (errors) => {
    if (!errors || typeof errors !== 'object') return null;
    const key = Object.keys(errors).find((k) => k === 'recipients' || k.startsWith('recipients.'));
    return key ? [].concat(errors[key])[0] : null;
};

// Works for both rejected errors (error.response) and handled interceptor results.
const extractErrorMessage = (source) => {
    const data = source?.response?.data || source?.data || {};
    return (
        firstRecipientsError(data.errors) ||
        (typeof data.errors === 'string' && data.errors) ||
        data.errors?.enabled?.[0] ||
        data.message ||
        source?.message ||
        'Failed to save notification setting'
    );
};

// The App.jsx axios interceptor already flashed these itself, even with suppressErrorFlash:
// 429 rate limit (rateLimited) and network/unknown errors (resolved with data: null, status 500).
const wasAlreadyFlashed = (response) => {
    if (!response) return false;
    // App.jsx flashes a 429 only when the body carries a message.
    if (response.rateLimited === true) return !!response.data?.message;
    return response.handled === true && response.data === null;
};

const showError = (source) => {
    if (wasAlreadyFlashed(source)) return;
    if (source?.rateLimited === true) {
        message.error(source.data?.message || 'Too many requests - please try again shortly');
        return;
    }
    message.error(extractErrorMessage(source));
};

const isFailed = (response) => !response || response.handled || response.status >= 400 || !response.data;

const NotificationSettingsTable = () => {
    const [settings, setSettings] = useState([]);
    const [recipientsInput, setRecipientsInput] = useState({});
    const [savingEventType, setSavingEventType] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get('/notification-settings', QUIET);
            if (isFailed(response)) {
                showError(response);
                return;
            }
            const rows = Array.isArray(response.data?.settings) ? response.data.settings : [];
            setSettings(rows);
            const inputs = {};
            rows.forEach((row) => {
                inputs[row.event_type] = recipientsToString(row.recipients);
            });
            setRecipientsInput(inputs);
        } catch (error) {
            showError(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const saveSetting = async (eventType, overrides = {}) => {
        const current = settings.find((row) => row.event_type === eventType);
        if (!current) return;

        // A toggle saves only the switch: it sends the persisted recipients, never unsaved text in the box.
        const isToggle = overrides.enabled !== undefined && overrides.recipients === undefined;
        const payload = {
            enabled: overrides.enabled !== undefined ? overrides.enabled : current.enabled,
            recipients:
                overrides.recipients !== undefined
                    ? overrides.recipients
                    : isToggle
                    ? current.recipients || []
                    : recipientsToArray(recipientsInput[eventType]),
        };

        setSavingEventType(eventType);
        try {
            const response = await axios.put(`/notification-settings/${eventType}`, payload, QUIET);
            if (isFailed(response)) {
                showError(response);
                return;
            }
            setSettings((prev) =>
                prev.map((row) => (row.event_type === eventType ? { ...row, ...response.data } : row))
            );
            if (!isToggle) {
                setRecipientsInput((prev) => ({
                    ...prev,
                    [eventType]: recipientsToString(response.data.recipients),
                }));
            }
            message.success(`${response.data.label || eventType} updated`);
        } catch (error) {
            showError(error);
        } finally {
            setSavingEventType(null);
        }
    };

    const handleToggle = (eventType, enabled) => {
        saveSetting(eventType, { enabled });
    };

    const handleRecipientsChange = (eventType, value) => {
        setRecipientsInput((prev) => ({ ...prev, [eventType]: value }));
    };

    const columns = [
        { title: 'Category', dataIndex: 'category', key: 'category', width: 140 },
        { title: 'Event', dataIndex: 'label', key: 'label', width: 260 },
        {
            title: 'Enabled',
            dataIndex: 'enabled',
            key: 'enabled',
            width: 100,
            render: (enabled, record) => (
                <Switch
                    checked={enabled}
                    loading={savingEventType === record.event_type}
                    onChange={(checked) => handleToggle(record.event_type, checked)}
                />
            ),
        },
        {
            title: 'Recipients',
            dataIndex: 'recipients',
            key: 'recipients',
            render: (_, record) => (
                <Input
                    placeholder="name@example.com, other@example.com"
                    value={recipientsInput[record.event_type] || ''}
                    onChange={(e) => handleRecipientsChange(record.event_type, e.target.value)}
                    onPressEnter={() => saveSetting(record.event_type)}
                />
            ),
        },
        {
            title: '',
            key: 'actions',
            width: 100,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    loading={savingEventType === record.event_type}
                    onClick={() => saveSetting(record.event_type)}
                >
                    Save
                </Button>
            ),
        },
    ];

    return (
        <Table
            rowKey="event_type"
            columns={columns}
            dataSource={settings}
            loading={isLoading}
            pagination={false}
        />
    );
};

export default NotificationSettingsTable;
